import XLSX from "xlsx-js-style";
import { formatDate, MOMENT_FORMAT_DATE_DMYHM_DOTS } from "./formatters";

const asText = (v) => (v == null || v === "" ? "" : String(v));

const resolveResearchCode = (row, serviceByIdCache = {}, planServices = []) => {
  const sid = Number(row?.serviceId ?? row?.service?.id);
  const fromRow =
    row?.service?.code ||
    row?.serviceCode ||
    row?.researchCode ||
    null;
  if (fromRow) return asText(fromRow);

  if (Number.isFinite(sid) && sid > 0) {
    const cached = serviceByIdCache?.[sid];
    if (cached?.code) return asText(cached.code);
  }

  const indexId = Number(row?.indexId);
  const fromPlanService = (planServices || []).find((ps) => {
    if (ps?._isDeleted) return false;
    const psSid = Number(ps?.serviceId ?? ps?.service?.id);
    const psIndexId = Number(ps?.indexId ?? ps?.IndexId);
    if (Number.isFinite(indexId) && indexId > 0 && psIndexId === indexId) {
      return true;
    }
    if (Number.isFinite(sid) && sid > 0 && psSid === sid) return true;
    return false;
  });
  const psCode =
    fromPlanService?.service?.code ||
    fromPlanService?.serviceCode ||
    null;
  if (psCode) return asText(psCode);

  // Не подставляем числовой serviceId как "код исследования"
  return "";
};

/**
 * Выгрузка отчёта по плану на согласовании СЛА:
 * показатели, коды исследований, план (шт/иссл./сумма).
 */
export function downloadPlanCodesAgreementReportXlsx({
  planId,
  fields = {},
  planMeta = {},
  planIndices = [],
  serviceByIdCache = {},
  planServices = [],
}) {
  const filialName =
    fields?.filial?.name || fields?.filialName || planMeta?.filial?.name || "";
  const contractName =
    fields?.contract?.name || fields?.contractName || planMeta?.contract?.name || "";
  const stateName =
    planMeta?.stateName ||
    (planMeta?.stateId != null ? `Статус ID ${planMeta.stateId}` : "");

  const metaRows = [
    ["План ID", asText(planId)],
    ["Филиал", filialName],
    ["Договор", contractName],
    ["Статус", stateName],
    ["Сформировано", formatDate(new Date(), MOMENT_FORMAT_DATE_DMYHM_DOTS)],
    [],
  ];

  const header = [
    "Показатель (наименование)",
    "Код исследования",
    "Лаборатория",
    "Группа",
    "План (планов)",
    "План (исслед.)",
    "План (сумма)",
  ];

  const dataRows = (planIndices || [])
    .filter((row) => row && !row._isDeleted)
    .map((row) => [
      asText(row.indexName || row.index?.name),
      resolveResearchCode(row, serviceByIdCache, planServices),
      asText(row.departmentName || row.department?.name),
      asText(row.groupName || row.group?.name || row.serviceGroup?.name),
      asText(row.plannedPlan),
      asText(row.plannedResearch),
      asText(row.plannedSum),
    ]);

  const aoa = [...metaRows, header, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "План на согласовании");

  const safeId = planId != null ? String(planId) : "plan";
  const fname = `План_на_согласовании_${safeId}_${formatDate(
    new Date(),
    MOMENT_FORMAT_DATE_DMYHM_DOTS
  )
    .replace(/[: ]/g, "_")
    .replace(/\./g, "")}.xlsx`;

  XLSX.writeFile(wb, fname);
}

/**
 * Сводный Excel по документу плана: все объекты + показатели.
 */
export function downloadPlanDocumentFormedXlsx({
  documentId,
  stateName = "",
  filialName = "",
  plans = [],
}) {
  const metaRows = [
    ["Документ плана ID", asText(documentId)],
    ["Филиал", asText(filialName)],
    ["Статус", asText(stateName)],
    ["Сформировано", formatDate(new Date(), MOMENT_FORMAT_DATE_DMYHM_DOTS)],
    [],
  ];

  const header = [
    "План ID",
    "Период",
    "Район/город",
    "Субъект",
    "ИИН/БИН",
    "Объект",
    "Местонахождение",
    "Лаборатория",
    "Количество проб",
    "Показатель",
    "Код исследования",
    "Группа",
    "План (сумма)",
  ];

  const formatPeriod = (plan) => {
    const raw = plan?.month || plan?.Month || plan?.year || plan?.Year;
    if (raw == null || raw === "") return "";
    if (typeof raw === "number" && raw >= 2000 && raw <= 2100) return String(raw);
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return asText(raw);
      const months = [
        "январь", "февраль", "март", "апрель", "май", "июнь",
        "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь",
      ];
      return `${months[d.getMonth()]} ${d.getFullYear()}`;
    } catch {
      return asText(raw);
    }
  };

  const dataRows = [];
  (plans || []).forEach((plan) => {
    const planId = plan?.id ?? plan?.Id;
    const period = formatPeriod(plan);
    const indices =
      plan?.indices ||
      plan?.planIndices ||
      [];
    const active = (indices || []).filter((r) => r && !r._isDeleted);
    if (!active.length) {
      dataRows.push([
        asText(planId),
        period,
        asText(plan?.districtCity),
        asText(plan?.inspectedEntityName),
        asText(plan?.individualOrBusinessId),
        asText(plan?.inspectedObjectName),
        asText(plan?.location),
        "",
        "",
        "",
        "",
        "",
        "",
      ]);
      return;
    }
    active.forEach((row) => {
      dataRows.push([
        asText(planId),
        period,
        asText(plan?.districtCity),
        asText(plan?.inspectedEntityName),
        asText(plan?.individualOrBusinessId),
        asText(plan?.inspectedObjectName),
        asText(plan?.location),
        asText(row.departmentName || row.department?.name),
        asText(row.plannedPlan),
        asText(row.indexName || row.index?.name),
        resolveResearchCode(row),
        asText(row.groupName || row.group?.name || row.serviceGroup?.name),
        asText(row.plannedSum),
      ]);
    });
  });

  const aoa = [...metaRows, header, ...dataRows];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Сформированный план");

  const safeId = documentId != null ? String(documentId) : "doc";
  const fname = `Сформированный_план_${safeId}_${formatDate(
    new Date(),
    MOMENT_FORMAT_DATE_DMYHM_DOTS
  )
    .replace(/[: ]/g, "_")
    .replace(/\./g, "")}.xlsx`;

  XLSX.writeFile(wb, fname);
}
